/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import {
  Form, Button, Row, Col, Spin, Input, Modal, DatePicker,
} from 'antd';
import { formatReceived } from 'utils/formatValues';
import { onError, onSuccess } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import Select from 'components/Select';
import { getEstadosGlobales } from 'api/catalogos/index';
import {
  getPreferenciaSPartidarias,
  getPreferenciaPartidaria,
  postPreferenciaPartidaria,
  patchPreferenciaPartidaria,
  deletePreferenciaPartidaria,
} from 'api/catalogos/PreferenciaPartidaria';
import { checkToken } from 'api/utils';
import { SLUG_PATTERN } from 'utils/patterns';
import {
  ESTATUS_ACTIVO,
  ESTATUS_INACTIVO,
  ESTATUS_AUTORIZADO,
} from 'utils/constants';
import { CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons';
import ModalConfirmation from 'components/ModalConfirmation';
import {
  cloneEstructuraJerarquica,
  createEstructuraJerarquica,
  deleteEstructuraJerarquica,
  getAllEstructurasJerarquicas,
  getEstructuraJerarquicaByID,
  updateEstructuraJerarquica,
} from 'api/catalogos/estructuraJerarquica';
import {
  DATE_FORMAT,
  DATE_FORMATS,
  DATE_FRONTEND_FORMAT,
} from 'utils/normalizers';
import { formsRules } from 'utils/rules';

export const motivoReclutamientoPermission = {
  permissionModel: 'estructurajerarquica',
  catalogsModels: [],
  tabsModels: [],
};

const EstructurasJerarquicas = ({ permission }) => {
  // #region useState

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState();
  const [isAddEditModalOpen, setIsAddEditModalOpen] = useState(false);
  const [isVisibleDeleteModal, setIsVisibleDeleteModal] = useState();
  const [estados, setEstados] = useState();
  const [estructurasJerarquicas, setEstructurasJerarquicas] = useState([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [disableAuth, setDisableAuth] = useState(false);
  const [deleteDisabled, setDeleteDisabled] = useState(false);
  const [disableForm, setDisableForm] = useState(false);
  const [visibleAuthConfirmation, setVisibleAuthConfirmation] = useState(false);
  const [visibleAlertInactive, setVisibleAlertInactive] = useState(false);
  const [isModalCloneDateVisible, setIsModalCloneDateVisible] = useState(false);
  const [isModalCloneConfirmationVisible, setIsModalCloneConfirmationVisible] = useState(false);
  const [form] = Form.useForm();
  // #endregion useState

  // #region variables
  const formInstances = [form];

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const columns = [
    {
      titleText: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      width: 100,
    },
    {
      titleText: 'Vigencia Inicio',
      dataIndex: 'vigencia_inicio',
      key: 'vigencia_inicio',
      width: 250,
    },
    {
      titleText: 'Vigencia Fin',
      dataIndex: 'vigencia_fin',
      key: 'vigencia_fin',
      width: 250,
    },
    {
      titleText: 'Estatus',
      dataIndex: 'estados_globales',
      key: 'estados_globales',
      width: 100,
      render: (val) => estados.find((e) => e.id === val)?.descripcion,
    },
  ];

  // #endregion variables

  // #region functions

  const fetchAll = async () => {
    setLoading(true);
    await checkToken();
    const [_estados, _estructurasJerarquicas] = await Promise.all([
      getEstadosGlobales(),
      getAllEstructurasJerarquicas(),
    ]);
    setEstados(_estados);
    setEstructurasJerarquicas(_estructurasJerarquicas);
    setLoading(false);
  };

  // const fetchData = async () => {
  //   setLoading(true);
  //   const _data = await getPreferenciaSPartidarias();
  //   setData(formatReceived(_data));
  //   setLoading(false);
  // };

  const onCancel = () => {
    setSelectedRowKeys([]);
    setDisableForm(false);
    setIsAddEditModalOpen(false);
  };

  const handleOnFinishConfirmation = async () => {
    try {
      setLoading(true);
      const [key] = selectedRowKeys;
      if (!key) {
        setLoading(false);
        onCancel();
        onSuccess(
          {},
          'No se puede Autorizar una Estructura Jerárquica ya Autorizada',
        );
        return;
      }

      const vals = {
        ...form.getFieldsValue(),
        vigencia_inicio: form.getFieldValue('vigencia_inicio')
          ? form.getFieldValue('vigencia_inicio').format(DATE_FORMATS[1])
          : null,
        vigencia_fin: form.getFieldValue('vigencia_fin')
          ? form.getFieldValue('vigencia_fin').format(DATE_FORMATS[1])
          : null,

        estados_globales: ESTATUS_AUTORIZADO,
      };
      const res = await updateEstructuraJerarquica(key, vals);
      if (res) {
        await fetchAll();
        onSuccess(res);
        setVisibleAuthConfirmation(false);
        onCancel();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const isDisabled = (rowEstatus) => {
    setDisableForm(rowEstatus !== ESTATUS_ACTIVO);
  };

  const onClickEdit = async (responseData = null) => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const match = responseData || estructurasJerarquicas.find((item) => item.id === key);
    const _estructura = await getEstructuraJerarquicaByID(match.id);
    setTimeout(() => {
      if (_estructura) {
        form.resetFields();
        setTimeout(() => {
          isDisabled(_estructura?.estados_globales);
          form.setFieldsValue(formatReceived(_estructura));
          setIsAddEditModalOpen(true);
        });
      }
    });
    setLoading(false);
  };

  const onFinish = async (_values, _continue = false) => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = {
        ...form.getFieldsValue(),
        vigencia_inicio: form.getFieldValue('vigencia_inicio')
          ? form.getFieldValue('vigencia_inicio').format(DATE_FORMATS[1])
          : null,
        vigencia_fin: form.getFieldValue('vigencia_fin')
          ? form.getFieldValue('vigencia_fin').format(DATE_FORMATS[1])
          : null,
      };
      const [key] = selectedRowKeys;
      const saved = await (key
        ? updateEstructuraJerarquica(key, values)
        : createEstructuraJerarquica(values));
      if (saved) {
        if (_continue) {
          await fetchAll();
          onClickEdit(saved);
        } else {
          onCancel();
          await fetchAll();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading, formInstances);
    }
  };

  const deleteItem = async () => {
    try {
      setLoading(true);
      if (selectedRowKeys.length) {
        const [key] = selectedRowKeys;
        const deleted = await deleteEstructuraJerarquica(key);
        if (deleted) {
          setIsVisibleDeleteModal(false);
          onCancel();
          await fetchAll();
        }
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    const isAutorizado = record?.estados_globales === ESTATUS_AUTORIZADO;
    const isInactivo = record?.estados_globales === ESTATUS_INACTIVO;
    setDeleteDisabled(isAutorizado);
    setDisableAuth(isAutorizado || isInactivo);
    form.setFieldsValue({
      ...record,
      vigencia_inicio: record.vigencia_inicio
        ? record.vigencia_inicio?.format(DATE_FORMATS[1])
        : null,
      vigencia_fin: record.vigencia_fin
        ? record.vigencia_fin?.format(DATE_FORMATS[1])
        : null,
    });
  };

  const onClickAdd = () => {
    onCancel();
    form.resetFields();
    setIsAddEditModalOpen(true);
  };

  const onClickDelete = () => {
    setIsVisibleDeleteModal(true);
  };

  // #endregion functions

  // #region useEffect

  useEffect(() => {
    fetchAll();
  }, []);
  // #endregion useEffect

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        <Table
          cols={columns}
          data={estructurasJerarquicas}
          rowSelection={rowSelection}
          handleOnRowClick={handleOnRowClick}
          controls={{
            onClickAdd,
            onClickEdit,
            onClickDelete,
          }}
          permission={permission}
          allowExportPDF={false}
          customActions={[
            {
              onClick: () => {
                setIsModalCloneConfirmationVisible(true);
              },
              text: 'Clonar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              disabled: !selectedRowKeys?.length,
            },
            {
              onClick: () => setVisibleAuthConfirmation(true),
              permission: { model: 'estructurajerarquica', action: 'autorizar' },
              text: 'Autorizar',
              icon: <CheckCircleOutlined />,
              disabledBySelection: false,
              className: 'success',
              // eslint-disable-next-line max-len
              disabled: !selectedRowKeys?.length || disableAuth,
            },
          ]}
          disableDelete={deleteDisabled}
        />
        <Modal
          open={isAddEditModalOpen}
          closable={false}
          maskClosable={!loading}
          footer={null}
          width={500}
          title={(
            <FormSubmitControls
              label={`${selectedRowKeys.length ? 'Editar' : 'Agregar'
              } Estructura Jerárquica`}
              onFinish={onFinish}
              onCancel={onCancel}
              loading={loading}
              allowSave={!selectedRowKeys?.length || !disableAuth}
            />
          )}
          onCancel={onCancel}
          forceRender
        >
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
            name="general"
            disabled={disableForm}
          >
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="nombre"
                label="Nombre de Estructura Jerárquica"
                rules={[formsRules.requiredRule]}
                // rules={requiredRule}
                hasFeedback
              >
                <Input placeholder="Ingresa un nombre" allowClear />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_inicio"
                label="Vigencia de Inicio"
                hasFeedback
              >
                <DatePicker format={DATE_FRONTEND_FORMAT} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="vigencia_fin"
                label="Vigencia de Fin"
                hasFeedback
              >
                <DatePicker format="DD/MM/YYYY" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={24}>
              <Form.Item
                name="estructura_jerarquica"
                label="Estructura Jerárquica"
                hasFeedback
              >
                <Select
                  labelProp="nombre" // lo que muestra
                  keyProp="id" // lo que manda (id numérico)
                  dataSource={estructurasJerarquicas}
                  allowClear
                  placeholder="Selecciona una estructura"
                />
              </Form.Item>
            </Col>

            <Col xs={24} sm={24} md={24}>
              <Form.Item
                name="estados_globales"
                label="Estatus"
                hasFeedback
                initialValue={ESTATUS_ACTIVO}
              >
                <Select
                  labelProp="descripcion"
                  keyProp="id"
                  activeInactive={!deleteDisabled}
                  dataSource={estados}
                  disabled={selectedRowKeys.length <= 0}
                  onChange={(val) => {
                    if (val === ESTATUS_INACTIVO) {
                      setVisibleAlertInactive(true);
                    }
                  }}
                />
              </Form.Item>
            </Col>
            <Form.Item hidden>
              <Button htmlType="submit" />
            </Form.Item>
          </Form>
        </Modal>
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => setIsVisibleDeleteModal(false)}
          open={isVisibleDeleteModal}
          content={`Estructurar Jerárquica ${form.getFieldValue('nombre')}`}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={() => setVisibleAlertInactive(false)}
          ConfirmInactive
          onCancel={() => {
            setVisibleAlertInactive(false);
            form.setFieldValue('estados_globales', ESTATUS_ACTIVO);
          }}
          open={visibleAlertInactive}
          contentTitle={`Inactivar la Estructura Jerárquica ${form.getFieldValue(
            'nombre',
          )}`}
          loading={loading}
        />

        <ModalConfirmation
          onFinish={handleOnFinishConfirmation}
          ConfirmAutorize
          onCancel={() => {
            setVisibleAuthConfirmation(false);
            onCancel();
          }}
          open={visibleAuthConfirmation}
          contentTitle={`Autorizar la Estructura Jerárquica ${form.getFieldValue(
            'nombre',
          )}`}
          loading={loading}
        />

        <ModalConfirmation
          title="Clonar estructura jerárquica"
          contentTitle={`Clonar la Estructura Jerárquica ${form.getFieldValue(
            'nombre',
          )}`}
          onFinish={async () => {
            setIsModalCloneConfirmationVisible(false);
            setIsModalCloneDateVisible(true);

            // try {
            //   setLoading(true);
            //   await cloneEstructuraJerarquica(selectedRowKeys[0]);
            //   await fetchAll();
            // } catch (error) {
            // } finally {
            //   setLoading(false);
            // }
          }}
          onFinishText="Continuar"
          onFinishIcon={<CheckCircleOutlined />}
          onCancel={() => setIsModalCloneConfirmationVisible(false)}
          onCancelIcon={<ArrowLeftOutlined />}
          loading={loading}
          open={isModalCloneConfirmationVisible}
        />

        <Modal
          width="500px"
          open={isModalCloneDateVisible}
          footer={null}
          onCancel={() => { }}
          title={(
            <FormSubmitControls
              label="Vigencia de estructura clonada"
              onFinish={async () => {
                try {
                  setLoading(true);
                  await cloneEstructuraJerarquica(selectedRowKeys[0]);
                  await fetchAll;
                  setIsModalCloneDateVisible(false);
                } catch (error) { /* ignore */ } finally {
                  setLoading(false);
                }
              }}
              onFinishText="Clonar"
              onCancel={() => setIsModalCloneDateVisible(false)}
              loading={loading}
            />
          )}
          forceRender
          centered
        >
          <Form
            layout="vertical"
            form={form}
            scrollToFirstError
          >
            <Form.Item
              name="vigencia_final"
              label="Vigencia"
            >
              <DatePicker
                placeholder=""
                format="DD/MM/YYYY"
              />
            </Form.Item>
          </Form>
        </Modal>
      </Spin>
    </Row>
  );
};

export default EstructurasJerarquicas;
